import styled from "@emotion/styled";
import { useBloc } from "@blac/react";
import type { FC } from "react";
import { useCallback } from "react";
import { useMediaQuery } from "@react-hooks-library/core";
import translate from "src/lib/translate";
import InDialog from "src/ui/components/InDialog/InDialog";
import useGoToOrBack from "src/ui/hooks/useGoToOrBack";
import useEscapeKey from "src/ui/hooks/useEscapeKey";
import WeightReadings from "../../WeightReadings/WeightReadings";
import WeightReadingsBloc from "../../WeightReadings/WeightReadingsBloc";
import { AppQueryPopupsController } from "../../AppQueryPopups/AppQueryPopupsBloc";
import { getStreakMode } from "../../WeightReadings/streakConfig";
import { APP_BREAKPOINT } from "src/constants/layout";
import { IconClose } from "src/constants/icons";

const DIALOG_WIDTH = "min(calc(100vw - 2rem), 36.75rem)";
const DAILY_DESKTOP_HEIGHT = "min(calc(100vh - 2rem), 62.5rem)";
const DAILY_MOBILE_HEIGHT = "100dvh";

const WEEKLY_DESKTOP_HEIGHT = "min(calc(100vh - 4rem), 31.5rem)";
const WEEKLY_DESKTOP_HEIGHT_STRIKE = "min(calc(100vh - 2rem), 36.5rem)";
const WEEKLY_MOBILE_HEIGHT = "60dvh";
const WEEKLY_MOBILE_HEIGHT_STRIKE = "75dvh";

const ContentPadding = styled.div`
  padding: 2rem 1.5rem 1.5rem;
`;

const DesktopHeader = styled.div`
  display: none;

  @media (min-width: ${APP_BREAKPOINT}px) {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.75rem;
    padding: 1.25rem 1.5rem;
    border-bottom: 1px solid var(--color-charcoal-12);
  }
`;

const DesktopTitle = styled.div`
  font-family: "STK Bureau";
  font-size: 1.25rem;
  font-style: normal;
  font-weight: 500;
  line-height: 140%;
  letter-spacing: -0.4px;
  color: var(--color-charcoal, #212121);
`;

const DesktopCloseButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2rem;
  height: 2rem;
  padding: 0;
  border: 0;
  border-radius: 999px;
  background: var(--color-charcoal-12);
  cursor: pointer;
  flex-shrink: 0;
  transition: background-color 0.15s ease;

  &:hover,
  &:active {
    background: var(--color-charcoal-22);
  }
`;

const WeightReadingsDialogStyled = styled(InDialog)`
  ion-content {
    --overflow: auto;
  }

  ion-content > *:last-child {
    padding-bottom: 2rem;
  }

  @media (min-width: ${APP_BREAKPOINT}px) {
    ion-header {
      display: none;
    }
  }
`;

const WeightReadingsDialog: FC<{
  returnUrl: string;
}> = ({ returnUrl }) => {
  const navigate = useGoToOrBack();
  const [state, { completedWeek }] = useBloc(WeightReadingsBloc);
  const isDesktop = useMediaQuery(`(min-width: ${APP_BREAKPOINT}px)`);
  const isWeekly = getStreakMode() === "weekly";
  const hasReachedStreak = isWeekly
    ? state.weeklyStreakCount > 3
    : completedWeek;
  const title = isWeekly
    ? translate("weeklyStreak.popup.title")
    : translate("dailyReadings.title");

  const onClose = useCallback(() => {
    AppQueryPopupsController.closePopup();
    navigate(returnUrl, {
      multiBack: true,
      replace: true
    });
  }, [navigate, returnUrl]);

  useEscapeKey(onClose);

  return (
    <WeightReadingsDialogStyled
      popup
      title={title}
      returnUrl={returnUrl}
      onClose={onClose}
      backdropDismiss={false}
      padding="none"
      width={DIALOG_WIDTH}
      height={
        isWeekly
          ? hasReachedStreak
            ? WEEKLY_DESKTOP_HEIGHT_STRIKE
            : WEEKLY_DESKTOP_HEIGHT
          : DAILY_DESKTOP_HEIGHT
      }
      mobileHeight={
        hasReachedStreak
          ? WEEKLY_MOBILE_HEIGHT_STRIKE
          : isWeekly
            ? WEEKLY_MOBILE_HEIGHT
            : DAILY_MOBILE_HEIGHT
      }
    >
      {isDesktop && (
        <DesktopHeader>
          <DesktopTitle>{title}</DesktopTitle>
          <DesktopCloseButton aria-label="Close" onClick={onClose}>
            <IconClose />
          </DesktopCloseButton>
        </DesktopHeader>
      )}
      <ContentPadding>
        <WeightReadings />
      </ContentPadding>
    </WeightReadingsDialogStyled>
  );
};

export default WeightReadingsDialog;
